import { CAMPING_YEAR, CLOSURE_WINDOWS, LAST_UPDATED_DISPLAY, LAST_UPDATED_ISO } from "@/constants";

/**
 * Closure dates (DESIGN.md): drenched panel in Tayrona's own color, a
 * 12-month strip and the three windows with their reasons. Every date is
 * derived from CLOSURE_WINDOWS, so nothing here can drift from the source.
 */

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

type MonthState = "open" | "closed" | "partial";

/** A month is "closed" if one window covers it whole-ish (15 days), "partial" if it touches it. */
function monthStates(): { label: string; state: MonthState; note?: string }[] {
  return MONTHS.map((label, i) => {
    const m = i + 1;
    const hit = CLOSURE_WINDOWS.find((w) =>
      w.startMonth === w.endMonth
        ? w.startMonth === m
        : m === w.startMonth || m === w.endMonth,
    );
    if (!hit) return { label, state: "open" };
    const spans = hit.startMonth !== hit.endMonth;
    const note =
      spans && m === hit.startMonth
        ? `${hit.startDay}-31`
        : spans
          ? `1-${hit.endDay}`
          : `${hit.startDay}-${hit.endDay}`;
    return { label, state: spans ? "partial" : "closed", note };
  });
}

const STATE_TEXT: Record<MonthState, string> = {
  open: "abierto",
  closed: "cierre",
  partial: "cierre parcial",
};

const ParkClosures = () => (
  <section id="cierres" aria-labelledby="closures-heading" className="pq-section">
    <div className="pq-wrap">
      <div className="pq-panel pq-tema-tayrona pq-panel-stack">
        <div className="pq-panel-body">
          <h2 id="closures-heading" className="pq-headline">
            Fechas de cierre del Parque Tayrona {CAMPING_YEAR}.
          </h2>
          <p className="pq-panel-text">
            El Parque Nacional Natural Tayrona cierra 3 veces al año por
            recuperación ecológica y respeto al calendario indígena. Consulta las
            fechas antes de reservar tu camping o alojamiento.
          </p>
          <p className="pq-panel-note">
            Última actualización:{" "}
            <time dateTime={LAST_UPDATED_ISO}>{LAST_UPDATED_DISPLAY}</time>
          </p>
        </div>

        <ol className="pq-months" aria-label={`Calendario de apertura ${CAMPING_YEAR}`}>
          {monthStates().map((m) => (
            <li key={m.label} className={`pq-month pq-month-${m.state}`}>
              <span className="pq-month-name">{m.label}</span>
              <span className="pq-month-note">
                {m.note ?? <span className="pq-sr">{STATE_TEXT[m.state]}</span>}
                {m.note ? <span className="pq-sr"> ({STATE_TEXT[m.state]})</span> : null}
              </span>
            </li>
          ))}
        </ol>

        <ul className="pq-closures">
          {CLOSURE_WINDOWS.map((w) => (
            <li key={w.full}>
              <p className="pq-title">Del {w.full}</p>
              <p className="pq-panel-note">{w.reason}</p>
            </li>
          ))}
        </ul>

        <p className="pq-panel-text pq-closures-warning">
          <strong>Sin excepciones.</strong> Durante los cierres no se puede entrar
          al parque bajo ninguna circunstancia. Si ya tienes reserva para esas
          fechas, habla directamente con el operador. Verifica siempre las fechas en{" "}
          <a
            href="https://www.parquesnacionales.gov.co/nuestros-parques/pnn-tayrona/"
            target="_blank"
            rel="noopener noreferrer"
            className="pq-link"
          >
            Parques Nacionales
          </a>{" "}
          antes de reservar.
        </p>
      </div>
    </div>
  </section>
);

export default ParkClosures;
